<script setup lang="ts">
import { computed } from 'vue';
import { RouterView } from 'vue-router';

import { UErrorView, useError } from '@support/core';

import AppNavRail from '@/shared/components/navigation/AppNavRail.vue';
import { useSwNavigationListener } from '@/shared/composables/useSwNavigationListener';
import { useVersionCheck } from '@/shared/composables/useVersionCheck';

const { error } = useError();
const hasError = computed(() => Boolean(error.value?.statusCode));
const isCriticalError = computed(() => error.value?.critical === true);

// PWA: keep the eternally-open window on the latest release and route
// service-worker notification clicks that reach uncontrolled windows.
useVersionCheck();
useSwNavigationListener();

// Views whose component instance survives navigation into a detail page, so
// scroll position and local view state are restored on the way back
// (Linear-style) instead of remounting from scratch. Matched against the
// component `name` each page declares via defineOptions.
//
// The ticket inbox is no longer listed: it owns `/tickets/:id` as a child
// route, so it simply stays mounted while threads change and never needs
// restoring.
const CACHED_PAGES: string[] = [];
</script>

<template>
  <!-- Critical error: hide entire layout, show only error -->
  <template v-if="hasError && isCriticalError">
    <UErrorView />
  </template>

  <!-- Normal layout -->
  <template v-else>
    <AppNavRail />

    <!-- min-w-0 lets the panel shrink to the free space next to the rail;
         without it wide content pushes the layout past the viewport instead of
         scrolling inside its own container. A fixed height + `overflow-hidden`
         make this the scroll boundary, so the inbox columns each scroll on
         their own instead of the page growing a single outer scrollbar.

         On desktop the workspace is an inset, rounded frame on the muted
         ground the rail shares (Linear's layout): 8px from every edge but the
         rail's, which is why the height gives back 1rem. Below `lg` there is
         no rail and it runs edge to edge. -->
    <div class="flex h-svh min-w-0 flex-1 flex-col overflow-hidden bg-default lg:my-2 lg:me-2 lg:h-[calc(100svh-1rem)] lg:rounded-[10px] lg:border lg:border-default">
      <!-- Non-critical error: show within layout -->
      <UErrorView v-if="hasError" />
      <RouterView
        v-else
        v-slot="{ Component }"
      >
        <KeepAlive :include="CACHED_PAGES">
          <component :is="Component" />
        </KeepAlive>
      </RouterView>
    </div>
  </template>
</template>
